import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Info, Server, Clock, Database, Cpu } from "lucide-react";
import api from "../../services/api";

interface HealthResponse {
  status: string;
  version: string;
  buildId: string;
  timestamp: string;
  environment: string;
  nodeVersion: string;
  uptime: number;
  database: {
    status: string;
  };
}

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const AboutTab: React.FC = () => {
  const { t } = useTranslation("admin");

  const {
    data: health,
    isLoading,
    error,
  } = useQuery<HealthResponse>(
    "app-health",
    () => api.health.checkHealth() as Promise<HealthResponse>,
    { staleTime: 30_000, refetchInterval: 60_000 },
  );

  if (isLoading) {
    return (
      <div className="admin-tab-content">
        <div className="loading">
          <div className="spinner" />
          {t("about.loading")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-tab-content">
        <div className="error-message">{t("about.error")}</div>
      </div>
    );
  }

  const version = health?.version || "0.0.0-dev";
  const isDevBuild = version === "0.0.0-dev";

  return (
    <div className="admin-tab-content">
      <div className="about-header">
        <h2>
          <Info size={20} /> {t("about.title")}
        </h2>
        <p className="about-subtitle">{t("about.subtitle")}</p>
      </div>

      <div className="about-grid">
        <div className="about-card">
          <div className="about-card-header">
            <Server size={16} />
            <span>{t("about.version")}</span>
          </div>
          <div className="about-card-value">
            {isDevBuild ? (
              <span className="version-dev">
                {version}
              </span>
            ) : (
              <span className="version-release">v{version}</span>
            )}
          </div>
        </div>

        <div className="about-card">
          <div className="about-card-header">
            <Cpu size={16} />
            <span>{t("about.build")}</span>
          </div>
          <div className="about-card-value">{health?.buildId || "local"}</div>
        </div>

        <div className="about-card">
          <div className="about-card-header">
            <Database size={16} />
            <span>{t("about.environment")}</span>
          </div>
          <div className="about-card-value">
            <span className={`env-badge env-${health?.environment || "development"}`}>
              {health?.environment || "development"}
            </span>
          </div>
        </div>

        <div className="about-card">
          <div className="about-card-header">
            <Clock size={16} />
            <span>{t("about.uptime")}</span>
          </div>
          <div className="about-card-value">
            {typeof health?.uptime === "number" ? formatUptime(health.uptime) : "—"}
          </div>
        </div>
      </div>

      <div className="about-details">
        <h3>{t("about.systemInfo")}</h3>
        <table className="about-table">
          <thead className="sr-only">
            <tr>
              <th scope="col">{t("about.property")}</th>
              <th scope="col">{t("about.value")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t("about.nodeVersion")}</td>
              <td>{health?.nodeVersion || "—"}</td>
            </tr>
            <tr>
              <td>{t("about.dbStatus")}</td>
              <td>
                <span
                  className={`status-dot ${health?.database?.status === "connected" ? "status-ok" : "status-error"}`}
                />
                {health?.database?.status || "unknown"}
              </td>
            </tr>
            <tr>
              <td>{t("about.serverStatus")}</td>
              <td>
                <span
                  className={`status-dot ${health?.status === "OK" ? "status-ok" : "status-error"}`}
                />
                {health?.status || "unknown"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AboutTab;
